import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { toast } from "@/components/ui/sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { useAuth } from "@/lib/auth/auth-context";
import { canManagePartnerGroups } from "@/lib/auth/roles";

import { useCreateGroup, useGroupsQuery } from "../api";
import { apiErrorDetail } from "../lib/errors";
import type { PartnerGroup } from "../types";

/**
 * Inline create-group form (name + optional license cap). Only rendered for
 * users who can actually create groups (super-user/admin — the `create`
 * endpoint is IsSuperUserOrAdmin); plain partners and local partners can't, so
 * the button is hidden rather than shown as a dead affordance.
 */
function CreateGroupForm() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const createGroup = useCreateGroup();
  const [name, setName] = useState("");
  const [maxLicenses, setMaxLicenses] = useState("");
  const [open, setOpen] = useState(false);
  // A partner with no limit of its own sets none on its groups either.
  const limitable = user?.max_group_licenses != null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    createGroup.mutate(
      {
        name: name.trim(),
        max_licenses: !limitable || maxLicenses === "" ? null : Number(maxLicenses),
      },
      {
        onSuccess: () => {
          // The create endpoint responds with PartnerGroupCreate (name +
          // max_licenses only — no id), so we can't deep-link into the new
          // group. The mutation invalidates the groups query; the new card
          // appears in the list below for the user to open.
          setName("");
          setMaxLicenses("");
          setOpen(false);
        },
        onError: (err) => toast.error(apiErrorDetail(err) ?? t("partners.actionFailed")),
      },
    );
  };

  if (!canManagePartnerGroups(user)) return null;

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}>
        <Plus />
        {t("partners.groups.newGroup")}
      </Button>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-lg">{t("partners.groups.newGroup")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="new-group-name">{t("partners.groups.nameLabel")}</Label>
            <Input
              id="new-group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("partners.groups.namePlaceholder")}
              required
            />
          </div>
          {limitable && (
            <div className="space-y-1.5">
              <Label htmlFor="new-group-max">{t("partners.groups.maxLicensesLabel")}</Label>
              <Input
                id="new-group-max"
                type="number"
                min={0}
                value={maxLicenses}
                onChange={(e) => setMaxLicenses(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                {t("partners.groups.maxLicensesHint")}
              </p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={createGroup.isPending || name.trim() === ""}>
              {createGroup.isPending
                ? t("partners.groups.creating")
                : t("partners.groups.create")}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              {t("partners.groups.cancel")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function GroupCard({
  group,
  onOpen,
}: {
  group: PartnerGroup;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const licenses =
    group.max_licenses != null
      ? `${group.license_count} / ${group.max_licenses}`
      : String(group.license_count);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {group.name || t("partners.groups.colName")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {group.partner_code && (
            <>
              <dt className="text-muted-foreground">
                {t("partners.groups.colCode")}
              </dt>
              <dd>{group.partner_code}</dd>
            </>
          )}
          <dt className="text-muted-foreground">{t("partners.groups.colMembers")}</dt>
          <dd>{group.member_count}</dd>
          <dt className="text-muted-foreground">{t("partners.groups.colLicenses")}</dt>
          <dd>{licenses}</dd>
          <dt className="text-muted-foreground">{t("partners.groups.colOwner")}</dt>
          <dd className="truncate">{group.owner_email ?? t("partners.groups.noCode")}</dd>
        </dl>
        <Button size="sm" onClick={onOpen}>
          {t("partners.groups.open")}
        </Button>
      </CardContent>
    </Card>
  );
}

/** Landing view: the caller's partner groups, plus create/open. */
export function PartnerGroupList({ onOpen }: { onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const groups = useGroupsQuery();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">{t("partners.groups.heading")}</h1>
          <p className="mt-1 text-muted-foreground">{t("partners.groups.subtitle")}</p>
        </div>
        <CreateGroupForm />
      </div>

      {groups.isLoading && (
        <p className="text-sm text-muted-foreground">{t("partners.loading")}</p>
      )}

      {groups.isError && (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-6">
            <p className="text-sm text-destructive">{t("partners.loadError")}</p>
            <Button variant="outline" size="sm" onClick={() => void groups.refetch()}>
              {t("partners.retry")}
            </Button>
          </CardContent>
        </Card>
      )}

      {groups.data && groups.data.length === 0 && (
        <Card>
          <CardContent className="space-y-1 py-6">
            <p className="font-medium">{t("partners.groups.empty")}</p>
            <p className="text-sm text-muted-foreground">
              {t("partners.groups.emptyHint")}
            </p>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {groups.data?.map((group) => (
          <GroupCard key={group.id} group={group} onOpen={() => onOpen(group.id)} />
        ))}
      </div>
    </div>
  );
}
